import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { api } from "@/api/client";
import { useMkRoot } from "@/app/branding";
import { recordRoute } from "@/app/navigation";
import { matchPages } from "@/app/pageSearch";
import type { SearchPage } from "@/app/pageSearch";
import { MkIcon } from "@/mk/MkIcon";

interface SearchItem {
    type?: string;
    key?: string;
    id: number;
    name: string;
    model: string;
    url?: string | false;
}

interface SearchGroup {
    key: string;
    model: string;
    label: string;
    color: number;
    recordCount: number;
    has_more: boolean;
    config_id: number;
    route?: string | false;
    icon?: string | false;
    items: SearchItem[];
}

interface SearchPayload {
    groups: SearchGroup[];
    min_query_length: number;
    limit: number;
}

interface PageEntry {
    kind: "page";
    index: number;
    page: SearchPage;
}

interface RecordEntry {
    kind: "record";
    index: number;
    item: SearchItem;
    group: SearchGroup;
}

type Entry = PageEntry | RecordEntry;

interface Props {
    initialTerm?: string;
    pages?: SearchPage[];
    onClose(): void;
}

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 180;
const MORE_LIMIT = 100;
const PAGE_LIMIT = 7;
const RECENT_KEY = "pm:global-search:recent";
const RECENT_MAX = 6;

function escapeRegExp(value: string) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function readRecent(): string[] {
    try {
        const raw = window.localStorage.getItem(RECENT_KEY);
        const parsed: unknown = raw ? JSON.parse(raw) : [];
        if (!Array.isArray(parsed)) {
            return [];
        }
        return parsed.filter((value): value is string => typeof value === "string").slice(0, RECENT_MAX);
    } catch {
        return [];
    }
}

function writeRecent(values: string[]) {
    try {
        window.localStorage.setItem(RECENT_KEY, JSON.stringify(values));
    } catch {
        // A full or disabled storage must never break the search dialog.
    }
}

function Highlight({ text, term }: { text: string; term: string }) {
    const needle = term.trim();
    if (!needle) {
        return <>{text}</>;
    }
    const parts = String(text).split(new RegExp(`(${escapeRegExp(needle)})`, "gi"));
    const lower = needle.toLocaleLowerCase();
    return (
        <>
            {parts.map((part, index) => (
                part.toLocaleLowerCase() === lower
                    ? <mark key={index} className="pm-search-mark">{part}</mark>
                    : <span key={index}>{part}</span>
            ))}
        </>
    );
}

function toneClass(color: number) {
    const safe = Number.isFinite(color) ? Math.abs(Math.trunc(color)) : 0;
    return `pm-search-tone-${safe % 12}`;
}

export function PmSearchDialog({ initialTerm = "", pages = [], onClose }: Props) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const root = useMkRoot();
    const dialogRef = useRef<HTMLDivElement | null>(null);
    const listRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const [term, setTerm] = useState(initialTerm);
    const [debounced, setDebounced] = useState(initialTerm.trim());
    const [active, setActive] = useState(0);
    const [expanded, setExpanded] = useState<Record<string, SearchItem[]>>({});
    const [loadingMore, setLoadingMore] = useState<string | null>(null);
    const [moreError, setMoreError] = useState<string | null>(null);
    const [recent, setRecent] = useState<string[]>(readRecent);

    useEffect(() => {
        const previous = document.activeElement as HTMLElement | null;
        const overflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        const timer = window.setTimeout(() => inputRef.current?.focus(), 0);
        return () => {
            window.clearTimeout(timer);
            document.body.style.overflow = overflow;
            previous?.focus?.();
        };
    }, []);

    useEffect(() => {
        const timer = window.setTimeout(() => setDebounced(term.trim()), DEBOUNCE_MS);
        return () => window.clearTimeout(timer);
    }, [term]);

    useEffect(() => {
        setActive(0);
        setExpanded({});
        setMoreError(null);
    }, [debounced]);

    const query = useQuery({
        queryKey: ["pm-global-search", debounced],
        enabled: debounced.length >= MIN_QUERY_LENGTH,
        queryFn: ({ signal }) => api.raw<SearchPayload>(
            `/search?q=${encodeURIComponent(debounced)}`, signal),
        staleTime: 30_000,
        placeholderData: keepPreviousData,
    });

    const payload = query.data?.data;
    const groups = useMemo(() => {
        const base = payload?.groups ?? [];
        return base.map((group) => {
            const extra = expanded[group.model];
            if (!extra) {
                return group;
            }
            return { ...group, items: extra, has_more: false, recordCount: extra.length };
        });
    }, [payload, expanded]);

    // Pages live in this tab already: no request, no waiting, and no page the
    // sidebar would have hidden.
    const pageHits = useMemo(
        () => matchPages(pages, term, PAGE_LIMIT), [pages, term]);

    const entries = useMemo<Entry[]>(() => {
        const flat: Entry[] = pageHits.map(
            (page, index) => ({ kind: "page", page, index }));
        for (const group of groups) {
            for (const item of group.items) {
                flat.push({ kind: "record", item, group, index: flat.length });
            }
        }
        return flat;
    }, [pageHits, groups]);

    const positions = useMemo(() => {
        const map = new Map<string, number>();
        for (const entry of entries) {
            if (entry.kind === "record") {
                map.set(`${entry.item.model}-${entry.item.id}`, entry.index);
            }
        }
        return map;
    }, [entries]);

    useEffect(() => {
        setActive((current) => Math.min(current, Math.max(entries.length - 1, 0)));
    }, [entries.length]);

    const serverMin = payload?.min_query_length ?? MIN_QUERY_LENGTH;
    const typing = term.trim();
    const shortQuery = typing.length > 0 && typing.length < serverMin;
    const ready = debounced.length >= MIN_QUERY_LENGTH;
    const empty = ready && !query.isFetching && !query.isError
        && groups.length === 0 && pageHits.length === 0;

    function remember(value: string) {
        const clean = value.trim();
        if (!clean) {
            return;
        }
        const next = [clean, ...recent.filter(
            (entry) => entry.toLocaleLowerCase() !== clean.toLocaleLowerCase())].slice(0, RECENT_MAX);
        setRecent(next);
        writeRecent(next);
    }

    function clearRecent() {
        setRecent([]);
        writeRecent([]);
    }

    function openItem(item: SearchItem) {
        const url = item.url || recordRoute(item.model, item.id, undefined, true);
        if (!url) {
            return;
        }
        remember(term);
        onClose();
        navigate(url);
    }

    function openPage(page: SearchPage) {
        remember(term);
        onClose();
        navigate(page.route);
    }

    function openEntry(entry: Entry) {
        if (entry.kind === "page") {
            openPage(entry.page);
        } else {
            openItem(entry.item);
        }
    }

    async function loadMore(group: SearchGroup) {
        setLoadingMore(group.model);
        setMoreError(null);
        try {
            const response = await api.raw<SearchPayload>(
                `/search?q=${encodeURIComponent(debounced)}`
                + `&model=${encodeURIComponent(group.model)}&limit=${MORE_LIMIT}`);
            const items = response.data.groups[0]?.items ?? [];
            setExpanded((current) => ({ ...current, [group.model]: items }));
        } catch {
            setMoreError(group.model);
        } finally {
            setLoadingMore(null);
        }
    }

    function scrollActiveIntoView(index: number) {
        window.setTimeout(() => {
            listRef.current
                ?.querySelector<HTMLElement>(`[data-search-index="${index}"]`)
                ?.scrollIntoView({ block: "nearest" });
        }, 0);
    }

    function moveActive(delta: number) {
        if (!entries.length) {
            return;
        }
        const next = (active + delta + entries.length) % entries.length;
        setActive(next);
        scrollActiveIntoView(next);
    }

    function onInputKeyDown(event: ReactKeyboardEvent<HTMLInputElement>) {
        if (event.key === "ArrowDown") {
            event.preventDefault();
            moveActive(1);
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            moveActive(-1);
        } else if (event.key === "Enter") {
            const entry = entries[active];
            if (entry) {
                event.preventDefault();
                openEntry(entry);
            }
        } else if (event.key === "Escape") {
            event.preventDefault();
            onClose();
        }
    }

    function trapFocus(event: ReactKeyboardEvent<HTMLDivElement>) {
        if (event.key !== "Tab") {
            return;
        }
        const nodes = dialogRef.current?.querySelectorAll<HTMLElement>(
            'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (!nodes || !nodes.length) {
            return;
        }
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus();
        }
    }

    const activeEntry = entries[active];
    const activeId = activeEntry ? `pm-search-option-${activeEntry.index}` : undefined;

    const pagesBlock = pageHits.length ? (
        <div className="pm-search-group" role="group" aria-label={t("search.pages")}>
            <div className="pm-search-group-head">
                <span className="pm-search-group-title">
                    <MkIcon name="fa-th-large" />
                    <span>{t("search.pages")}</span>
                    <span className="pm-search-count">{pageHits.length}</span>
                </span>
            </div>
            {pageHits.map((page, index) => (
                <button key={page.id}
                        id={`pm-search-option-${index}`}
                        type="button"
                        role="option"
                        aria-selected={index === active}
                        data-search-index={index}
                        className={`pm-search-item${index === active ? " is-active" : ""}`}
                        onMouseEnter={() => setActive(index)}
                        onClick={() => openPage(page)}>
                    <MkIcon name={page.icon} className="pm-search-item-icon" />
                    <span className="pm-search-item-name">
                        <Highlight text={page.label} term={typing} />
                        <span className="pm-search-item-hint">{page.hint}</span>
                    </span>
                    <MkIcon name="fa-arrow-right" className="pm-search-item-go" />
                </button>
            ))}
        </div>
    ) : null;

    let records: ReactNode;
    if (shortQuery) {
        records = (
            <div className="pm-search-state pm-search-state-quiet">
                <MkIcon name="fa-search" />
                <p>{t("search.keepTyping", { count: serverMin })}</p>
            </div>
        );
    } else if (query.isError) {
        records = (
            <div className="pm-search-state">
                <MkIcon name="fa-exclamation-circle" />
                <p>{t("search.error")}</p>
                <button type="button" className="pm-search-retry"
                        onClick={() => void query.refetch()}>
                    {t("search.retry")}
                </button>
            </div>
        );
    } else if (query.isLoading) {
        records = (
            <div className="pm-search-skeleton" aria-hidden="true">
                {[0, 1, 2, 3].map((row) => <span key={row} />)}
            </div>
        );
    } else if (empty) {
        records = (
            <div className="pm-search-state">
                <MkIcon name="fa-search" />
                <p>{t("search.nothing")}</p>
                <span>{t("search.nothingHint")}</span>
            </div>
        );
    } else {
        records = groups.map((group) => (
            <div key={group.model} className="pm-search-group" role="group" aria-label={group.label}>
                <div className={`pm-search-group-head ${toneClass(group.color)}`}>
                    <span className="pm-search-group-title">
                        <MkIcon name={group.icon || "fa-file-text-o"} />
                        <span>{group.label}</span>
                        <span className="pm-search-count">
                            {group.recordCount}{group.has_more ? "+" : ""}
                        </span>
                    </span>
                    {group.has_more && (
                        <button type="button" className="pm-search-more"
                                disabled={loadingMore === group.model}
                                onClick={() => void loadMore(group)}>
                            {loadingMore === group.model
                                ? t("search.loadingMore")
                                : t("search.more")}
                        </button>
                    )}
                </div>
                {moreError === group.model && (
                    <div className="pm-search-more-error">{t("search.error")}</div>
                )}
                {group.items.map((item) => {
                    const index = positions.get(`${item.model}-${item.id}`) ?? -1;
                    return (
                        <button key={`${item.model}-${item.id}`}
                                id={index >= 0 ? `pm-search-option-${index}` : undefined}
                                type="button"
                                role="option"
                                aria-selected={index === active}
                                data-search-index={index}
                                className={`pm-search-item${index === active ? " is-active" : ""}`}
                                onMouseEnter={() => index >= 0 && setActive(index)}
                                onClick={() => openItem(item)}>
                            <span className="pm-search-item-name">
                                <Highlight text={item.name} term={debounced} />
                            </span>
                            <MkIcon name="fa-arrow-right" className="pm-search-item-go" />
                        </button>
                    );
                })}
            </div>
        ));
    }

    let body: ReactNode;
    if (!typing) {
        body = recent.length ? (
            <div className="pm-search-section">
                <div className="pm-search-section-head">
                    <span>{t("search.recent")}</span>
                    <button type="button" className="pm-search-link" onClick={clearRecent}>
                        {t("search.clearRecent")}
                    </button>
                </div>
                {recent.map((value) => (
                    <button key={value} type="button" className="pm-search-recent"
                            onClick={() => {
                                setTerm(value);
                                inputRef.current?.focus();
                            }}>
                        <MkIcon name="fa-clock-o" />
                        <span>{value}</span>
                    </button>
                ))}
            </div>
        ) : (
            <div className="pm-search-state pm-search-state-quiet">
                <MkIcon name="fa-search" />
                <p>{t("search.typeToSearch")}</p>
                <span>{t("search.typeToSearchHint")}</span>
            </div>
        );
    } else {
        body = (
            <>
                {pagesBlock}
                {records}
            </>
        );
    }

    return createPortal(
        <div className="mk pm-search-root" {...root}>
            <div className="pm-search-scrim" onMouseDown={onClose} />
            <div className="pm-search-dialog" role="dialog" aria-modal="true"
                 aria-label={t("search.title")} ref={dialogRef} onKeyDown={trapFocus}>
                <div className="pm-search-field">
                    <MkIcon name="fa-search" className="pm-search-field-icon" />
                    <input ref={inputRef}
                           className="pm-search-input"
                           type="text"
                           value={term}
                           placeholder={t("search.placeholder")}
                           role="combobox"
                           aria-autocomplete="list"
                           aria-expanded={entries.length > 0}
                           aria-controls="pm-search-results"
                           aria-activedescendant={activeId}
                           autoComplete="off"
                           spellCheck={false}
                           onChange={(event) => setTerm(event.target.value)}
                           onKeyDown={onInputKeyDown} />
                    {query.isFetching && <span className="pm-search-spinner" aria-hidden="true" />}
                    {term && (
                        <button type="button" className="pm-search-clear"
                                aria-label={t("search.clear")}
                                onClick={() => {
                                    setTerm("");
                                    inputRef.current?.focus();
                                }}>
                            <MkIcon name="fa-times" />
                        </button>
                    )}
                    <button type="button" className="pm-search-close"
                            aria-label={t("search.close")} onClick={onClose}>
                        <kbd>Esc</kbd>
                    </button>
                </div>
                <div className="pm-search-body" id="pm-search-results" role="listbox"
                     aria-label={t("search.results")} ref={listRef}>
                    {body}
                </div>
                <div className="pm-search-foot">
                    <span><kbd>↑</kbd><kbd>↓</kbd> {t("search.hintNavigate")}</span>
                    <span><kbd>↵</kbd> {t("search.hintOpen")}</span>
                    <span><kbd>Esc</kbd> {t("search.hintClose")}</span>
                </div>
            </div>
        </div>,
        document.body,
    );
}
